import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { array, number, object, string } from 'yup';
import { permissionsApi } from 'src/api/permissions';
import { pimApi } from 'src/api/pimApi';
import { Button } from 'src/common';
import { RHFProvider, RHFTextField } from 'src/common/rhf';
import {
  mapActivePermissions,
  type ActivePermission,
} from 'src/pages/subscriptions/productPermissionModules';
import { CataloguePicker } from './CataloguePicker';
import { cycleFromItems, mapCatalogueItems } from './catalogue';
import {
  MAX_LICENSES,
  MIN_LICENSES,
  type CatalogueItem,
  type SubscriptionCreateDraft,
  type SubscriptionCreateFormValues,
} from './types';

const defaultValues: SubscriptionCreateFormValues = {
  customerReference: '',
  internalNotes: '',
  items: [],
};

const validationSchema = object({
  customerReference: string().trim().optional(),
  internalNotes: string().trim().optional(),
  items: array()
    .of(
      object({
        productId: string().required(),
        licenses: number()
          .typeError('Enter number of licenses')
          .integer('Licenses must be a whole number')
          .min(MIN_LICENSES, `Number of licenses must be at least ${MIN_LICENSES}`)
          .max(MAX_LICENSES, `Number of licenses must be at most ${MAX_LICENSES}`)
          .required(),
      }),
    )
    .min(1, 'Select at least one product or package')
    .required(),
});

interface SubscriptionCreateFormProps {
  clientId: string;
  clientName: string;
  initialDraft?: SubscriptionCreateDraft | null;
  onNext: (draft: SubscriptionCreateDraft) => void;
  onCancel?: () => void;
}

export const SubscriptionCreateForm = ({
  clientId,
  clientName,
  initialDraft,
  onNext,
  onCancel,
}: SubscriptionCreateFormProps) => {
  const theme = useTheme();
  const [showOptionalFields, setShowOptionalFields] = useState(
    Boolean(initialDraft?.customerReference || initialDraft?.internalNotes),
  );
  const [catalogue, setCatalogue] = useState<CatalogueItem[]>([]);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const [activePermissions, setActivePermissions] = useState<ActivePermission[]>([]);

  const methods = useForm<SubscriptionCreateFormValues>({
    defaultValues: initialDraft
      ? {
          customerReference: initialDraft.customerReference,
          internalNotes: initialDraft.internalNotes,
          items: initialDraft.items.map((item) => ({
            productId: item.productId,
            licenses: item.licenses,
          })),
        }
      : defaultValues,
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema) as any,
  });

  const { control, handleSubmit, setValue } = methods;
  const items = useWatch({ control, name: 'items' }) ?? [];

  useEffect(() => {
    let mounted = true;
    setCatalogueLoading(true);

    pimApi
      .getProductPackageGroup({
        groupBy: 'msrpKind',
        isActive: true,
        page: 0,
        pageSize: 200,
      })
      .then((response) => {
        if (!mounted) return;
        setCatalogue(mapCatalogueItems(response));
      })
      .catch(() => {
        if (!mounted) return;
        setCatalogue([]);
        toast.error('Failed to load products and packages');
      })
      .finally(() => {
        if (mounted) setCatalogueLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    permissionsApi
      .getAll(0, 1000, true)
      .then((response) => {
        if (!mounted) return;
        const rows = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setActivePermissions(mapActivePermissions(rows));
      })
      .catch(() => {
        if (!mounted) return;
        setActivePermissions([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const onSubmit = (values: SubscriptionCreateFormValues) => {
    const subscriptionType = cycleFromItems(values.items, catalogue);
    if (!subscriptionType) {
      toast.error('Select at least one monthly or yearly product or package');
      return;
    }

    onNext({
      clientId,
      clientName,
      customerReference: values.customerReference?.trim() || '',
      internalNotes: values.internalNotes?.trim() || '',
      subscriptionType,
      items: values.items.map((item) => {
        const option = catalogue.find((entry) => entry.value === item.productId);
        return {
          productId: item.productId,
          licenses: Number(item.licenses) || MIN_LICENSES,
          name: option?.label || item.productId,
          entityType: option?.entityType === 'PACKAGE' ? 'PACKAGE' : 'PRODUCT',
        };
      }),
    });
  };

  return (
    <RHFProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Typography
        style={{
          fontSize: 13,
          color: theme.palette.text.secondary,
          marginBottom: 16,
        }}>
        Add products and packages for {clientName || 'this client'}
      </Typography>

      <CataloguePicker
        catalogue={catalogue}
        loading={catalogueLoading}
        selected={items}
        onChange={(next) => setValue('items', next, { shouldDirty: true, shouldValidate: true })}
        activePermissions={activePermissions}
      />

      <Box style={{ marginTop: 16 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showOptionalFields}
              onChange={(event) => setShowOptionalFields(event.target.checked)}
            />
          }
          label={
            <Typography style={{ fontSize: 13, fontWeight: 600 }}>
              Show more fields (optional)
            </Typography>
          }
          sx={{ ml: 0 }}
        />
      </Box>

      {showOptionalFields ? (
        <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 12 }}>
          <RHFTextField
            name="customerReference"
            outerLabel="CUSTOMER REFERENCE"
            placeholder="Enter customer reference"
          />
          <RHFTextField
            name="internalNotes"
            outerLabel="INTERNAL NOTES"
            placeholder="Enter notes"
            multiline
            rows={3}
          />
        </Box>
      ) : null}

      <Box
        display="flex"
        justifyContent={onCancel ? 'space-between' : 'flex-end'}
        alignItems="center"
        style={{ marginTop: 24 }}>
        {onCancel ? (
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            sx={{ minWidth: 120, height: 42 }}>
            Cancel
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={items.length === 0 || catalogueLoading}
          sx={{ minWidth: 120, height: 42, borderRadius: '8px', fontWeight: 700 }}>
          Next
        </Button>
      </Box>
    </RHFProvider>
  );
};
